import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { PowerBESSLogo } from "../components/PowerBESSLogo";
import { sendToTeam } from "../lib/formSubmit";
import "../styles/BacktestRequest.css";

export default function BacktestRequest() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [form, setForm] = useState({
    fullName: "",
    company: "",
    role: "",
    email: "",
    phone: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit = form.fullName && form.company && form.role && form.email;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      await sendToTeam({
        _subject: `[Backtest] New request — ${form.company}`,
        "Full name": form.fullName,
        Role: form.role,
        Company: form.company,
        "Work email": form.email,
        Phone: form.phone || "(not provided)",
        "Submitted at": new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
        Source: "/backtest-request",
      });
      // local backup so leads aren't lost if FormSubmit goes down later
      try {
        const existing = JSON.parse(localStorage.getItem("powerbess_leads") || "[]");
        existing.push({ ...form, submittedAt: new Date().toISOString() });
        localStorage.setItem("powerbess_leads", JSON.stringify(existing));
      } catch (e) {}
      setSubmitted(true);
    } catch (err) {
      setSubmitError(
        "Couldn't send your request right now. Please email team@powerbess.ai directly — we'll respond within 48 hours."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="brq-wrap">
        <Helmet><title>Backtest Request Received — PowerBESS.ai</title></Helmet>
        <BrqHeader />
        <div className="brq-success">
          <div className="brq-success-icon"><Check size={28} strokeWidth={3} /></div>
          <h1 className="brq-success-title">Request received.</h1>
          <p className="brq-success-sub">
            We've logged your details. Our team will reach out within 48 hours with a custom backtest deck based on your site specs.
          </p>
          <div className="brq-success-meta">
            <div><span className="brq-meta-l">Company</span><span className="brq-meta-v">{form.company}</span></div>
            <div><span className="brq-meta-l">Contact</span><span className="brq-meta-v">{form.email}</span></div>
          </div>
          <button className="brq-btn-primary" onClick={() => navigate("/")} data-testid="brq-back-home">
            <ArrowLeft size={15}/> Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="brq-wrap">
      <Helmet>
        <title>Request a Free BESS Backtest on IEX Data — PowerBESS.ai</title>
        <meta name="description" content="Get a free, custom BESS arbitrage backtest on actual IEX DAM/RTM/GDAM data. 6-slide deck within 48 hours, no commitment. For IPPs, traders, and renewable energy developers in India." />
        <meta name="keywords" content="BESS backtest, battery storage backtest India, IEX arbitrage backtest, custom dispatch simulation, PowerBESS demo" />
        <link rel="canonical" href="https://powerbess.ai/backtest-request" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Request a free BESS backtest — PowerBESS.ai" />
        <meta property="og:description" content="Custom backtest on your site's actual IEX data, delivered in 48 hours. No commitment." />
        <meta property="og:url" content="https://powerbess.ai/backtest-request" />
        <meta property="og:image" content="https://powerbess.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Request a free BESS backtest — PowerBESS.ai" />
        <meta name="twitter:description" content="Custom backtest on your site's actual IEX data, delivered in 48 hours." />
      </Helmet>

      <BrqHeader />

      <div className="brq-main">
        {/* Left: context */}
        <aside className="brq-aside">
          <div className="brq-aside-tag">FREE · NO COMMITMENT</div>
          <h1 className="brq-aside-title">Request a custom backtest on your site's actual IEX data.</h1>
          <p className="brq-aside-sub">
            We'll model how PowerBESS would have dispatched your battery against the last 30–90 days of real IEX prices — and show you the revenue gap between your current approach and what's possible.
          </p>
          <ul className="brq-aside-list">
            <li><span className="brq-tick">✓</span> 6-slide custom deck within 48 hours</li>
            <li><span className="brq-tick">✓</span> Real DAM / RTM / GDAM data, your site, your battery</li>
            <li><span className="brq-tick">✓</span> Oracle-PnL comparison + DSM exposure analysis</li>
            <li><span className="brq-tick">✓</span> No NDA required — we sign yours if you need one</li>
          </ul>
        </aside>

        {/* Right: single-step form */}
        <form className="brq-form" onSubmit={submit} data-testid="brq-form">
          <div className="brq-step-body">
            <div className="brq-row brq-row-2">
              <Field label="Full name" required
                     value={form.fullName} onChange={(v) => update("fullName", v)}
                     placeholder="Anish Patel" testId="brq-fullname"/>
              <Field label="Role" required
                     value={form.role} onChange={(v) => update("role", v)}
                     placeholder="VP of Storage" testId="brq-role"/>
            </div>
            <Field label="Company" required
                   value={form.company} onChange={(v) => update("company", v)}
                   placeholder="e.g. ACME Solar Holdings" testId="brq-company"/>
            <div className="brq-row brq-row-2">
              <Field label="Work email" type="email" required
                     value={form.email} onChange={(v) => update("email", v)}
                     placeholder="you@company.com" testId="brq-email"/>
              <Field label="Phone (optional)" type="tel"
                     value={form.phone} onChange={(v) => update("phone", v)}
                     placeholder="+91 98xxx xxxxx" testId="brq-phone"/>
            </div>
            <p className="brq-disclaimer">By submitting, you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>. We won't share your details with third parties.</p>
            {submitError && (
              <div className="brq-error" role="alert" data-testid="brq-error">{submitError}</div>
            )}
            <div className="brq-nav">
              <Link to="/" className="brq-btn-ghost"><ArrowLeft size={15}/> Cancel</Link>
              <button type="submit" className="brq-btn-primary" disabled={!canSubmit || submitting} data-testid="brq-submit">
                {submitting ? "Sending…" : <>Submit request <ArrowRight size={15}/></>}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function BrqHeader() {
  return (
    <header className="brq-header">
      <Link to="/" className="brq-logo" data-testid="brq-logo">
        <PowerBESSLogo size={28}/>
        <span>Power<span className="brq-a">BESS</span>.ai</span>
      </Link>
      <Link to="/" className="brq-back" data-testid="brq-header-back">
        <ArrowLeft size={14}/> Back
      </Link>
    </header>
  );
}

function Field({ label, type = "text", required, value, onChange, placeholder, testId }) {
  return (
    <div className="brq-field">
      <label className="brq-label">{label}{required && <span className="brq-req">*</span>}</label>
      <input
        className="brq-input"
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </div>
  );
}
